/**
 * intents/index.js — the C3 "How to Say It" library (§2.1-B).
 *
 * Dependency-free on purpose, exactly like marketing/pages.js: the Node
 * build-time prerender and sitemap generator import this, and neither can
 * resolve React or JSX.
 *
 * Each entry is one exact-moment search ICP #5 actually types. The structure is
 * fixed by §2.1-B: the situation, one paragraph of politeness-theory
 * credibility, a tuned draft per relevant persona, and the CTA.
 *
 * The example emails are the whole argument. Two postures for the same facts
 * shows what "tone as a coordinate" means without a demo — and it is the part
 * a competitor writing a listicle cannot copy, because they have no personas.
 *
 * Claims discipline (§4): no invented statistics, no fabricated testimonials.
 * Brown & Levinson politeness theory and Labov are real, cited scholarship and
 * are the only research referenced.
 */

export const INTENTS = [
  {
    slug: 'say-no-to-a-request',
    title: 'How to Say No in an Email (Without Burning the Bridge) | Persona',
    description: 'The exact wording for declining a request at work — two example emails, why saying no is hard, and how to keep the relationship intact.',
    h1: 'How to say no in an email without burning the bridge',
    sub: 'Declining is rarely about the words. It is about giving the other person somewhere to go.',
    situation: [
      'Someone has asked you for something you cannot or should not do. The request is reasonable, the person is reasonable, and you still have to refuse. The temptation is to over-explain — three paragraphs of justification that read as guilt and invite negotiation.',
      'A good no is short, unambiguous, and offers a path. It does not apologise four times, and it does not leave the door ajar when the answer is closed.',
    ],
    psychology: 'Brown and Levinson call a refusal a <strong>face-threatening act</strong>: it threatens the other person&rsquo;s desire to be approved of and their desire to be unimpeded. Most people compensate by piling on hedges, which paradoxically reads as insincere &mdash; the reader senses the padding and starts probing for the real reason. The reliable repair is not more softening but <em>one</em> clear redress move: an alternative, a reason stated once, or an explicit statement that you value the relationship.',
    drafts: [
      { persona: 'The Diplomat', when: 'Ongoing relationship you need to protect',
        email: `Subject: Re: Design review Thursday

Hi Priya,

I can't take this one on — my next two weeks are committed to the Atlas migration and I'd be doing it badly.

Two things that might help: Marcus has done three of these and knows the template, and if it can wait until the 22nd I'd be glad to take it properly.

Sorry to add a step to your week.

— Sam` },
      { persona: 'The Executive', when: 'The answer is simply no',
        email: `Subject: Re: Design review Thursday

Hi Priya,

I can't do this one — I'm committed to Atlas through the 20th.

Marcus knows the template and has capacity.

— Sam` },
    ],
    avoid: [
      '<strong>Apologising more than once.</strong> A second sorry reads as an opening, and the reader will push.',
      '<strong>"Unfortunately I am unable to..."</strong> Formality at the moment of refusal signals discomfort, not respect.',
      '<strong>A maybe you do not mean.</strong> "Let me see what I can do" costs you the same no, later, with interest.',
    ],
    faq: [
      { q: 'Should I explain why I am saying no?', a: 'Once, briefly, and only if the reason is real. A single concrete reason ("I am committed to Atlas through the 20th") closes the question. Three reasons invite the reader to solve them one at a time.' },
      { q: 'Is it rude to decline without offering an alternative?', a: 'No, but an alternative does the repair work for you. If you have one, it converts a refusal into a redirect. If you do not, do not invent one.' },
    ],
    related: [
      { href: '/how-to/decline-a-meeting', label: 'How to decline a meeting politely' },
      { href: '/how-to/push-back-on-a-deadline', label: 'How to push back on a deadline' },
      { href: '/email-tone', label: 'Change the tone of an email' },
    ],
  },

  {
    slug: 'push-back-on-a-deadline',
    title: 'How to Ask to Move a Deadline (Email Examples) | Persona',
    description: 'How to push a deadline without losing trust — two example emails, the psychology of why late notice hurts more than the delay, and what to include.',
    h1: 'How to push back on a deadline without losing trust',
    sub: 'The delay is rarely the problem. Finding out late is the problem.',
    situation: [
      'The date is not going to happen. You knew a few days ago, and every day you wait makes the message harder to send and worse to receive.',
      'The email that works does three things quickly: names the new date, says what the extra time buys, and confirms what has <em>not</em> changed. Vagueness here reads as trouble, even when there is none.',
    ],
    psychology: 'A slipped deadline threatens what politeness theory calls <strong>negative face</strong> &mdash; the other person&rsquo;s freedom to plan. That is why late notice stings more than the delay itself: it removes their options rather than the outcome. The repair is to hand options back. A specific new date, a reason that shows the time is being spent on their behalf, and an explicit list of what is unaffected restores the ability to plan, which is what was actually taken.',
    drafts: [
      { persona: 'The Diplomat', when: 'Client or someone senior who will be disappointed',
        email: `Subject: Meridian launch — moving to Wednesday 27th

Hi Sarah,

I want to flag this early rather than on Friday: the launch is moving from the 22nd to Wednesday the 27th.

The five days are going into QA on the mobile checkout flow. Nothing is wrong — we found two edge cases and I would rather catch them now than in your customers' hands.

Budget and scope are unchanged. I can have a private preview link with you Thursday, and I am glad to walk you through it if that is useful.

— Sam` },
      { persona: 'The Veteran', when: 'Internal team, no diplomacy tax needed',
        email: `Subject: Launch moves to Wed 27th

Quick one: launch is Wednesday the 27th, not Friday the 22nd.

The extra days go to mobile checkout QA — two edge cases we would rather not ship.

Scope and budget unchanged. Preview link Thursday.

— Sam` },
    ],
    avoid: [
      '<strong>Waiting for certainty.</strong> A heads-up with a rough date beats a precise one sent late.',
      '<strong>Blaming named people.</strong> "Two of our devs were out" reads as an excuse and exposes your team.',
      '<strong>Leaving the new date open.</strong> "Early next week" invites a second conversation you do not want.',
    ],
    faq: [
      { q: 'How much notice is enough?', a: 'As soon as you believe it more likely than not. People forgive a delay flagged early and remember one flagged late.' },
      { q: 'Should I apologise?', a: 'Once, and briefly. The reassurance that scope and budget are unchanged does more work than an apology does.' },
    ],
    related: [
      { href: '/how-to/deliver-bad-news-to-a-client', label: 'How to deliver bad news to a client' },
      { href: '/how-to/apologise-for-a-mistake', label: 'How to apologise in an email' },
    ],
  },

  {
    slug: 'ask-for-a-raise',
    title: 'How to Ask for a Raise Over Email (With Examples) | Persona',
    description: 'How to ask for a compensation review by email — two example messages, what to include, and why asking for a conversation beats asking for a number.',
    h1: 'How to ask for a raise in an email',
    sub: 'The email is not where you win the raise. It is where you get the meeting.',
    situation: [
      'You have the case. What you do not have is a way to open it that does not feel like an ultimatum or an apology.',
      'The best version of this email is short and asks for a <em>conversation</em>, not a number. It names two or three specific things you have done since the last review and proposes a time.',
    ],
    psychology: 'Asking for money threatens your own face as much as your manager&rsquo;s &mdash; which is why most drafts hedge until the ask disappears. Labov&rsquo;s work on how speakers shift register under social pressure shows the pattern: the more consequential the request, the more people retreat into indirect language, and the less likely the listener is to register that a request was made at all. Specificity is the antidote. Concrete contributions and a concrete meeting ask read as confidence, not aggression.',
    drafts: [
      { persona: 'The Analyst', when: 'Evidence-led manager, structured culture',
        email: `Subject: Compensation review — 30 minutes?

Hi Priya,

I would like to schedule a conversation about my compensation.

Since my last review I led the Atlas migration to a zero-downtime cutover, and I am currently onboarding both new engineers on the team. Market data for this level runs meaningfully above my current base.

Would 30 minutes this month work? I am flexible on timing and happy to send context in advance.

— Sam` },
      { persona: 'The Diplomat', when: 'Relationship-led manager, less formal culture',
        email: `Subject: Can we find 30 minutes this month?

Hi Priya,

I would like to talk about where my compensation sits.

The last two quarters have looked different from the ones before — the Atlas migration, and now bringing both new hires up to speed. It feels like the right moment to look at it together.

Any 30 minutes that suits you this month. Happy to share the detail beforehand so the time is useful.

— Sam` },
    ],
    avoid: [
      '<strong>Naming a number in the first email.</strong> It anchors the conversation before you have had it.',
      '<strong>Citing a competing offer you do not have.</strong> It works once and costs you the relationship.',
      '<strong>Listing everything.</strong> Two or three specifics land; ten read as a case for the defence.',
    ],
    faq: [
      { q: 'Email or in person?', a: 'Email to get the meeting, meeting to have the conversation. A written ask gives your manager time to prepare, which is usually to your advantage.' },
      { q: 'What if I am told to wait for the cycle?', a: 'Ask what specifically would make it a yes at the cycle, and get it in writing. That converts a deferral into a commitment.' },
    ],
    related: [
      { href: '/how-to/ask-your-boss-for-time-off', label: 'How to ask your boss for time off' },
      { href: '/for/executives', label: 'Persona for executives' },
    ],
  },

  {
    slug: 'decline-a-meeting',
    title: 'How to Decline a Meeting Politely (Email Examples) | Persona',
    description: 'How to get out of a meeting without seeming difficult — two example emails and what to offer instead so the work still gets done.',
    h1: 'How to decline a meeting politely',
    sub: 'Declining lands badly when it looks like you are avoiding the work rather than the meeting.',
    situation: [
      'A meeting has appeared with no agenda, and attending it costs you the only uninterrupted block in your day. Saying no to a colleague&rsquo;s meeting reads as a judgement on their time unless you handle it carefully.',
      'The move is to decline the <em>format</em>, not the topic, and to make the alternative easier than the meeting would have been.',
    ],
    psychology: 'A meeting invitation is a claim on your time that has already been made publicly &mdash; the calendar entry exists before you answer. Declining therefore reverses a decision rather than preventing one, which is a stronger face threat than a plain refusal. Offering a faster channel does the redress: it signals that you are declining the thirty minutes, not the person or the problem.',
    drafts: [
      { persona: 'The Catalyst', when: 'Peer, low stakes, keep it light',
        email: `Subject: Re: Quick sync Thursday

Hi Dan,

Going to drop off this one — I am heads-down on the launch until Friday.

Drop the questions in the Slack thread and I will answer them same day. If it is visual, send a two-minute Loom and I will reply properly.

— Sam` },
      { persona: 'The Executive', when: 'Recurring meeting you need out of permanently',
        email: `Subject: Re: Weekly platform sync

Hi Dan,

I am going to step out of this one going forward — it overlaps with the only focus block I have.

I will read the notes and pick up anything that needs me. If something needs a decision from me, flag it directly and I will turn it around same day.

— Sam` },
    ],
    faq: [
      { q: 'Do I need to give a reason?', a: 'One clause is enough. The alternative you offer does more work than the reason does.' },
      { q: 'What if it is my manager?', a: 'Ask what outcome they need rather than declining outright. Often the meeting is a proxy for a question you can answer in writing.' },
    ],
    related: [
      { href: '/how-to/say-no-to-a-request', label: 'How to say no in an email' },
    ],
  },

  {
    slug: 'chase-an-unanswered-email',
    title: 'How to Follow Up on an Unanswered Email | Persona',
    description: 'How to chase a reply without sounding passive-aggressive — two example follow-ups and how long to wait before sending.',
    h1: 'How to follow up on an email nobody answered',
    sub: 'The silence usually is not personal. Your follow-up should assume that.',
    situation: [
      'You sent something that needed a reply. Nothing. Now the follow-up has to chase without accusing, because nine times out of ten the message is simply buried.',
      'The effective follow-up makes replying cheap: it restates the ask in one line and gives a default that happens if they do nothing.',
    ],
    psychology: 'A follow-up carries an implied accusation &mdash; that the reader failed to act &mdash; which is why so many read as passive-aggressive even when the words are neutral. Removing the implication is mostly structural: attribute the silence to circumstance rather than choice, and reduce the cost of replying. A stated default converts the reply from an obligation into an option, which is the difference between nagging and a nudge.',
    drafts: [
      { persona: 'The Facilitator', when: 'First follow-up, three to five days later',
        email: `Subject: Re: Q3 budget sign-off

Hi Rob,

Bumping this in case it got buried — no urgency implied.

The one thing I need is a yes or no on the £14k line. If it is easier, I can assume yes and proceed Thursday unless you say otherwise.

— Sam` },
      { persona: 'The Executive', when: 'Second follow-up, something is now blocked',
        email: `Subject: Re: Q3 budget sign-off — blocking Thursday

Hi Rob,

This one is now holding up Thursday's start.

I need a yes or no on the £14k line. If I do not hear back by end of Wednesday I will proceed on the assumption it is approved.

— Sam` },
    ],
    avoid: [
      '<strong>"Just circling back."</strong> It says nothing and signals you have counted the days.',
      '<strong>"As per my last email."</strong> Universally read as annoyance, whatever you intended.',
      '<strong>Following up daily.</strong> It trains people to wait for the next one.',
    ],
    faq: [
      { q: 'How long should I wait?', a: 'Three to five working days for a normal ask. Sooner only if something is genuinely blocked, and then say what is blocked.' },
      { q: 'How many follow-ups?', a: 'Two, then change channel or escalate. A third email rarely produces what the first two did not.' },
    ],
    related: [
      { href: '/how-to/cold-email-a-stranger', label: 'How to cold email someone' },
      { href: '/for/sales', label: 'Persona for sales' },
    ],
  },

  {
    slug: 'apologise-for-a-mistake',
    title: 'How to Apologise in a Work Email (Examples) | Persona',
    description: 'How to apologise at work without over-apologising — two example emails and why what changes next matters more than how sorry you sound.',
    h1: 'How to apologise for a mistake at work',
    sub: 'A good apology is mostly about what happens next.',
    situation: [
      'Something went wrong and it was yours. The instinct is either to bury it in context or to apologise so thoroughly that the reader ends up reassuring you.',
      'What the other person needs is short: acknowledgement without hedging, what changes, and by when. Their confidence is restored by the plan, not by the contrition.',
    ],
    psychology: 'An apology is unusual among face-threatening acts because it threatens the <em>sender&rsquo;s</em> face rather than the recipient&rsquo;s. That is why over-apologising is so common and so counterproductive: the extra hedging is self-directed repair, and it shifts the emotional labour onto the person you wronged, who now has to manage your discomfort as well as their own problem.',
    drafts: [
      { persona: 'The Facilitator', when: 'Client relationship, second slip',
        email: `Subject: Re: Second delay — what changes now

Hi Dana,

You are right, and I am not going to explain it away. Twice this quarter you have found out late, and that is on how I have been running this.

Two things change starting now: a status email from me every Friday whether there is news or not, and a shared tracker you can open any time without asking me.

I have 15 minutes Monday morning if you want to talk it through.

— Sam` },
      { persona: 'The Executive', when: 'Internal, small mistake, keep it brief',
        email: `Subject: My error on the invoice numbers

Hi Rob,

The Q3 figures I sent Tuesday were wrong — I pulled from the pre-adjustment sheet. Corrected version attached.

I have added a check step before anything goes out. Sorry for the rework.

— Sam` },
    ],
    avoid: [
      '<strong>"I am sorry if..."</strong> A conditional apology is not one.',
      '<strong>Explaining before acknowledging.</strong> Context reads as defence when it arrives first.',
      '<strong>Naming who else was involved.</strong> It is heard as deflection even when it is true.',
    ],
    faq: [
      { q: 'Should I explain what happened?', a: 'Briefly, and after the acknowledgement. The explanation should serve their understanding of the fix, not your defence.' },
      { q: 'What if it was not entirely my fault?', a: 'Apologise for your part, specifically. A precise apology is more credible than a broad one and does not require you to accept what is not yours.' },
    ],
    related: [
      { href: '/how-to/deliver-bad-news-to-a-client', label: 'How to deliver bad news to a client' },
      { href: '/how-to/push-back-on-a-deadline', label: 'How to move a deadline' },
    ],
  },

  {
    slug: 'deliver-bad-news-to-a-client',
    title: 'How to Deliver Bad News to a Client by Email | Persona',
    description: 'How to tell a client something has gone wrong — two example emails, and why leading with the news beats leading with the context.',
    h1: 'How to deliver bad news to a client',
    sub: 'Lead with the news. Everything you put in front of it reads as a stall.',
    situation: [
      'The number is worse, the date has moved, or the thing they were counting on will not happen. Your draft opens with two paragraphs of background because that feels kinder.',
      'It is not. A client reading around for the actual news is doing work you should have done, and the delay reads as evasion the moment they find it.',
    ],
    psychology: 'Burying bad news is a politeness strategy that misfires in writing. In conversation, preamble gives the listener time to adjust; in email the reader scans for the point, finds it in paragraph three, and retrospectively reads everything before it as management. Leading with the news and following with the plan reverses that: the reader knows where they stand by line two and reads the rest as help rather than as cushioning.',
    drafts: [
      { persona: 'The Diplomat', when: 'Long-term client, recoverable situation',
        email: `Subject: Migration slipping to the 27th — plan inside

Hi Dana,

The migration will not be done by the 20th. The new date is the 27th.

We found a data-integrity issue in the legacy records that has to be resolved before cutover — shipping on time would have meant shipping it into your live system.

What this does not change: budget, scope, and your team's access throughout. I will send a daily one-liner until we are through it, and I have 15 minutes any morning this week if you want to talk.

— Sam` },
      { persona: 'The Veteran', when: 'Client who prefers no ceremony',
        email: `Subject: Migration date moving to the 27th

Hi Dana,

Not going to make the 20th. New date is the 27th.

Data-integrity issue in the legacy records — has to be fixed before cutover or it lands in your live system.

Budget and scope unchanged. Daily update from me until it is done.

— Sam` },
    ],
    faq: [
      { q: 'Should I call instead?', a: 'For anything that changes money or a committed date, send the email first so they have it in writing, then offer the call. Do not use a call to avoid putting it on record.' },
      { q: 'How much detail about the cause?', a: 'Enough to show the delay is being spent on their behalf. Beyond that it becomes self-justification.' },
    ],
    related: [
      { href: '/how-to/apologise-for-a-mistake', label: 'How to apologise for a mistake' },
      { href: '/for/consultants', label: 'Persona for consultants' },
    ],
  },

  {
    slug: 'ask-your-boss-for-time-off',
    title: 'How to Ask Your Boss for Time Off (Email Examples) | Persona',
    description: 'How to request leave by email — two examples, and why stating the dates plainly works better than asking permission.',
    h1: 'How to ask your boss for time off',
    sub: 'Ask about the impact, not about permission.',
    situation: [
      'You have dates in mind and you are drafting something apologetic. The apologetic version invites scrutiny, because it implies you are unsure whether you should be asking.',
      'The version that works states the dates, shows you have thought about coverage, and asks about the impact rather than for approval.',
    ],
    psychology: 'Requests phrased tentatively invite the reader to supply the missing confidence &mdash; usually by asking questions you did not want. In politeness terms an over-hedged request signals that you consider the imposition large, which primes your manager to treat it that way. Stating dates plainly while showing coverage frames it as a planning conversation rather than a favour.',
    drafts: [
      { persona: 'The Analyst', when: 'Formal workplace, advance notice',
        email: `Subject: Leave request — 14 to 22 August

Hi Priya,

I would like to take leave from the 14th to the 22nd of August.

The Atlas work will be through its final review by the 11th, and Marcus can cover anything that comes in on the Halden account — he has been shadowing it since June.

Anything on your side that makes those dates difficult?

— Sam` },
      { persona: 'The Facilitator', when: 'Shorter notice or a busy period',
        email: `Subject: Two days in August — 21st and 22nd

Hi Priya,

I am hoping to take the 21st and 22nd off. Family commitment I would rather not move.

Both days are clear of anything client-facing, and I will get the Halden update out on the 20th so nothing is waiting.

Does that cause you any problems?

— Sam` },
    ],
    faq: [
      { q: 'Do I have to give a reason?', a: 'Usually not, and offering one invites it to be weighed. "A family commitment" is sufficient where any reason is expected at all.' },
      { q: 'What if it is short notice?', a: 'Acknowledge it in one clause and lead with coverage. Short notice is forgiven when the gap is already filled.' },
    ],
    related: [
      { href: '/how-to/ask-for-a-raise', label: 'How to ask for a raise' },
    ],
  },

  {
    slug: 'give-critical-feedback',
    title: 'How to Give Critical Feedback in an Email | Persona',
    description: 'How to give hard feedback in writing — two example emails, and why the feedback sandwich fails on the page.',
    h1: 'How to give critical feedback in an email',
    sub: 'In writing, the sandwich does not work. The reader eats the bread and misses the filling.',
    situation: [
      'You have to tell someone their work is not good enough, and you are writing it rather than saying it. Written criticism has no tone of voice to soften it and no chance to read the room mid-sentence.',
      'What works is specificity, a clear standard, and a route to fixing it. Vagueness intended as kindness lands as a cloud the person cannot act on.',
    ],
    psychology: 'The praise-criticism-praise structure survives in speech because delivery marks which part carries the weight. On the page every sentence carries equal weight, so the reader &mdash; motivated to hear the kinder reading &mdash; takes the praise as the message and the criticism as a caveat. Naming the specific gap against a specific standard removes the ambiguity the sandwich creates.',
    drafts: [
      { persona: 'The Facilitator', when: 'Someone you manage, first conversation',
        email: `Subject: The Halden deck — a rework before Thursday

Hi Marcus,

The Halden deck needs another pass before Thursday, and I want to be specific about what.

The analysis is solid. What is missing is the recommendation — slides 8 to 14 walk through the options but never say which one we think they should pick, and that is the thing Dana will be looking for.

Can you add a recommendation slide up front and cut two of the options? Happy to look at a draft tomorrow if that helps.

— Sam` },
      { persona: 'The Executive', when: 'Peer or a repeat issue',
        email: `Subject: Halden deck — needs a recommendation

Hi Marcus,

The deck does not make a recommendation. Slides 8 to 14 present four options and stop.

Dana needs to know what we think she should do. Please add a recommendation slide at the front and cut it to two options.

Thursday is the deadline — send me a draft tomorrow if you want a look first.

— Sam` },
    ],
    avoid: [
      '<strong>"Just my two cents."</strong> If it needs acting on, do not label it optional.',
      '<strong>"Some people have noticed..."</strong> Anonymous sourcing turns feedback into a rumour.',
      '<strong>Saving it all for the review.</strong> Feedback delivered late is heard as a case being built.',
    ],
    faq: [
      { q: 'Should hard feedback be email at all?', a: 'Anything about performance or conduct is better in person. Email suits specific, work-product feedback where a written record helps them act on it.' },
      { q: 'How do I avoid sounding harsh?', a: 'Be precise rather than gentle. A clear gap and a clear route out reads as respect; vagueness reads as disappointment.' },
    ],
    related: [
      { href: '/how-to/say-no-to-a-request', label: 'How to say no in an email' },
      { href: '/for/executives', label: 'Persona for executives' },
    ],
  },

  {
    slug: 'negotiate-a-price',
    title: 'How to Negotiate Price Over Email (With Examples) | Persona',
    description: 'How to push back on a quote or defend your own — two example emails and why anchoring on value beats anchoring on the number.',
    h1: 'How to negotiate a price by email',
    sub: 'Whoever mentions a number first should also mention what it buys.',
    situation: [
      'Either the quote is too high and you have to push back, or yours has been challenged and you have to hold it. Both drafts tend to collapse into a number with an apology attached.',
      'The email that works separates the number from the scope. If the price cannot move, the scope can — and saying so keeps the conversation alive without discounting.',
    ],
    psychology: 'Price conversations are where hedging costs the most. An unhedged number reads as a position; the same number wrapped in qualifiers reads as an opening bid, and the reader will treat it as one. Attaching the number to what it buys gives it a reason to exist that is not simply your preference, which is much harder to negotiate against than a bare figure.',
    drafts: [
      { persona: 'The Analyst', when: 'Defending your own price',
        email: `Subject: Re: Proposal — scope and price

Hi Dana,

I can hold £24k, and I would rather adjust scope than the number.

The figure covers discovery, the migration itself, and four weeks of post-cutover support. If the budget is fixed at £18k, the cleanest cut is the support window — that takes it to £18,500 and keeps the migration itself untouched.

What I would not recommend is trimming discovery. That is where the last two projects found the problems that would otherwise have surfaced at cutover.

Happy to talk it through.

— Sam` },
      { persona: 'The Diplomat', when: 'Asking someone else to come down',
        email: `Subject: Re: Quote for the Q4 work

Hi Rob,

The proposal looks right in shape. The number is above what I can approve without going up a level, which I would rather avoid for a first project together.

If we dropped the monthly reporting pack — useful, but we have our own dashboards — where would that land? If that gets us close to £18k I can sign this week.

— Sam` },
    ],
    faq: [
      { q: 'Should I give a number first?', a: 'If you have a defensible one, yes. Anchoring works, and a number with a scope attached is much harder to negotiate down than a bare figure.' },
      { q: 'How do I decline a discount without losing the deal?', a: 'Offer a smaller scope at the lower price. It keeps the conversation on value and protects your rate for the next client.' },
    ],
    related: [
      { href: '/how-to/cold-email-a-stranger', label: 'How to cold email someone' },
      { href: '/for/consultants', label: 'Persona for consultants' },
    ],
  },

  {
    slug: 'resign-gracefully',
    title: 'How to Write a Resignation Email (Examples) | Persona',
    description: 'How to resign by email without burning anything down — two examples, what to include, and what to leave out.',
    h1: 'How to resign without burning the bridge',
    sub: 'The resignation email is a record. Write it for the version of you that needs a reference in four years.',
    situation: [
      'You are leaving. Whatever the reason, the email itself should be short, warm, and factual: the date, the offer to hand over, and a genuine thank-you if you have one.',
      'This is not the document for grievances. Anything you want to say about why belongs in the conversation, where it can be heard rather than filed.',
    ],
    psychology: 'A resignation is one of the few work emails that is read more than once, by people who were not on the original thread. That changes what the writing is for: it is a record as much as a message. Warmth costs nothing here and is disproportionately remembered, while a single sharp line outlives every good year that preceded it.',
    drafts: [
      { persona: 'The Diplomat', when: 'Good relationship, leaving on good terms',
        email: `Subject: Resignation — last day 3 October

Hi Priya,

I am writing to let you know I am resigning. My last day will be Friday 3 October, which gives us the full notice period.

I want to leave things in good shape. I will have the Atlas documentation finished and Marcus fully briefed on Halden well before then — happy to shape the handover however is most useful to you.

Thank you for the last three years, and particularly for backing me on the migration when it was not obviously going to work.

— Sam` },
      { persona: 'The Executive', when: 'Formal, or a relationship that is already strained',
        email: `Subject: Resignation — last day 3 October

Hi Priya,

Please accept this as notice of my resignation. My last day will be Friday 3 October.

I will complete the Atlas documentation and hand over the Halden account before then. Let me know how you would like the handover structured.

Thank you for the opportunity.

— Sam` },
    ],
    avoid: [
      '<strong>Explaining where you are going.</strong> It is not required and invites a conversation you did not choose.',
      '<strong>Listing grievances.</strong> The exit conversation is the place, if anywhere is.',
      '<strong>Offering an open-ended notice period.</strong> Name a date; ambiguity here always costs you.',
    ],
    faq: [
      { q: 'Should I tell my manager before emailing?', a: 'Yes, wherever possible. The email should confirm a conversation, not deliver the news.' },
      { q: 'How much notice?', a: 'Whatever your contract requires, stated as a specific date rather than a duration.' },
    ],
    related: [
      { href: '/how-to/give-critical-feedback', label: 'How to give critical feedback' },
    ],
  },

  {
    slug: 'cold-email-a-stranger',
    title: 'How to Cold Email Someone (Examples That Get Replies) | Persona',
    description: 'How to write a cold email that gets answered — two examples, and why one hook, one proof point and one ask beats a paragraph of context.',
    h1: 'How to cold email a stranger',
    sub: 'One hook, one proof point, one ask. Everything else is why it did not get read.',
    situation: [
      'You are writing to someone who does not know you and owes you nothing. They will decide in about two seconds, and most of that decision is made on the first line.',
      'The structure that survives contact: a reason you are writing to <em>them</em> specifically, one concrete thing that makes you credible, and a small ask with a date attached.',
    ],
    psychology: 'A cold email is a maximal imposition with no relationship to draw on, so every conventional politeness move &mdash; preamble, flattery, extended context &mdash; costs you attention you have not yet earned. Brevity does the redress work instead: a short message visibly respects the reader&rsquo;s time, and a small, specific ask is easier to grant than a vague large one.',
    drafts: [
      { persona: 'The Visionary', when: 'Pitching a service or partnership',
        email: `Subject: Invoice processing at Delta Freight

Hi Elena,

You mentioned on the Freight Ops panel that invoice reconciliation is still the bottleneck at month end. That is the problem we work on.

We took invoice processing from nine days to thirty-six hours for Harbor Line and TQ Systems — both mid-size logistics, both without replacing their ERP.

Worth fifteen minutes on Thursday to see whether it maps to your setup?

— Sam` },
      { persona: 'The Analyst', when: 'Asking an expert for their time',
        email: `Subject: Question about your zero-downtime cutover post

Hi Elena,

Your write-up on the zero-downtime cutover at Harbor Line is the only honest account of that pattern I have found — particularly the part about the read-replica lag.

We are about to do something similar on a smaller dataset and I have one specific question about how you handled the write freeze.

Would you be open to fifteen minutes, or a reply here if that is easier?

— Sam` },
    ],
    avoid: [
      '<strong>"I hope this email finds you well."</strong> It marks the message as a template before the second line.',
      '<strong>Explaining your company before your reason for writing.</strong> Nobody reads paragraph two of a stranger.',
      '<strong>"Let me know if you would like to chat."</strong> An ask with no date is a decision they can defer forever.',
    ],
    faq: [
      { q: 'How long should a cold email be?', a: 'Under 120 words. If it needs more, the ask is too large for a first contact.' },
      { q: 'Is it worth following up?', a: 'Once, after five to seven days, adding something new rather than repeating the first email.' },
    ],
    related: [
      { href: '/how-to/chase-an-unanswered-email', label: 'How to follow up on an unanswered email' },
      { href: '/for/sales', label: 'Persona for sales' },
    ],
  },
];

export const INTENT_BY_SLUG = Object.fromEntries(INTENTS.map((i) => [i.slug, i]));

/** Route metadata for the sitemap and prerender, mirroring MARKETING_PAGES. */
export const INTENT_ROUTES = INTENTS.map((i) => ({
  path: `/how-to/${i.slug}`,
  title: i.title,
  description: i.description,
  changefreq: 'monthly',
  priority: 0.7,
}));
